/* eslint-disable jsx-a11y/anchor-is-valid */
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import AppDownloadBanner from 'src/components/AppDownloadBanner';
import { toAbsoluteUrl } from '../../_metronic/helpers';
import { APP_ANDROID_URL, APP_IOS_URL, detectMobilePlatform, getAppDownloadUrl, isMobileOrTabletDevice } from './app-download';
import { ChangePassword } from './components/ChangePassword';
import { ForgotPassword } from './components/ForgotPassword';
import { Login } from './components/Login';
import { NewPassword } from './components/NewPassword';
import { Registration } from './components/Registration';
import { RoundedQRCode } from './components/RoundedQRCode';

function StoreButtons({ className, forceMobileLayout }: { className?: string; forceMobileLayout?: boolean }) {
  const platform = detectMobilePlatform();
  // Mweb: cihaza göre tek link. Masaüstü (other): her iki mağaza da görünür.
  const showIos = Boolean(APP_IOS_URL) && (platform === 'ios' || platform === 'other');
  const showAndroid = Boolean(APP_ANDROID_URL) && (platform === 'android' || platform === 'other');

  if (!showIos && !showAndroid) return null;

  return (
    <div className={clsx('d-flex flex-row gap-3 w-100', className)}>
      {showIos && (
        <a href={APP_IOS_URL} target="_blank" rel="noopener noreferrer" className="btn btn-white border border-gray-300 d-flex align-items-center justify-content-center gap-2 flex-grow-1">
          <img src={toAbsoluteUrl('/media/svg/brand-logos/apple-black.svg')} alt="" style={{ height: 20 }} />
          {/* mobil / tablet */}
          <span className={clsx('fw-bold fs-7 ps-2 text-start lh-sm', forceMobileLayout ? 'd-inline-block' : 'd-inline-block d-xxl-none')}>
            App Store'dan
            <span className="text-primary d-block">İndir</span>
          </span>
          {/* desktop */}
          {!forceMobileLayout && (
            <span className="fw-bold fs-7 ps-2 text-start d-none d-xxl-inline">
              App Store'dan <span className="text-primary">İndir</span>
            </span>
          )}
        </a>
      )}
      {showAndroid && (
        <a href={APP_ANDROID_URL} target="_blank" rel="noopener noreferrer" className="btn btn-white border border-gray-300 d-flex align-items-center justify-content-center gap-2 flex-grow-1">
          <img src={toAbsoluteUrl('/media/svg/brand-logos/google-play-store.svg')} alt="" style={{ height: 20 }} />
          {/* mobil / tablet */}
          <span className={clsx('fw-bold fs-7 ps-2 text-start lh-sm', forceMobileLayout ? 'd-inline-block' : 'd-inline-block d-xxl-none')}>
            Google Play'den
            <span className="text-primary d-block">İndir</span>
          </span>
          {/* desktop */}
          {!forceMobileLayout && (
            <span className="fw-bold fs-7 ps-2 text-start d-none d-xxl-inline">
              Google Play'den <span className="text-primary">İndir</span>
            </span>
          )}
        </a>
      )}
    </div>
  );
}

function AuthSideCard({ isMobileDevice }: { isMobileDevice: boolean }) {
  const downloadUrl = getAppDownloadUrl();
  const hasQr = Boolean(downloadUrl);
  // QR yalnızca gerçek masaüstünde; tablet/telefon mweb gibi davranır.
  const showQr = hasQr && !isMobileDevice;

  return (
    <div
      className={clsx(
        'w-100 d-flex flex-column bg-white border border-gray-200 rounded shadow-sm overflow-hidden p-4',
        isMobileDevice ? 'align-items-center text-center' : 'w-xxl-350px align-items-center align-items-xxl-stretch text-center text-xxl-start'
      )}
    >
      {/* begin::Heading */}
      <div className="px-6 pt-6 pb-2">
        <h2 className="text-primary fw-bolder fs-xxl-4 fs-6 mb-2">idefix Satıcı Uygulamasını İndirin</h2>
        <p className="text-gray-600 fs-xxl-6 fs-7 mb-0">Siparişlerinizi yönetin, stoklarınızı güncelleyin ve satışlarınızı her yerden takip edin.</p>
      </div>
      {/* end::Heading */}

      {/* begin::Mağaza butonları */}
      <div className={clsx(!isMobileDevice && 'px-xxl-6')}>
        <StoreButtons forceMobileLayout={isMobileDevice} />
      </div>
      {/* end::Mağaza butonları */}

      {/* begin::QR (yalnızca geniş masaüstü) */}
      {showQr && (
        <div className="d-none d-xxl-block px-6 py-4">
          <div className="bg-light rounded p-4 d-flex flex-row align-items-center gap-3">
            <div className="bg-white border border-gray-300 rounded p-1 d-inline-flex flex-shrink-0">
              <RoundedQRCode value={downloadUrl} size={90} />
            </div>
            <div className="flex-grow-1">
              <div className="text-dark fw-bolder fs-6 mb-1">QR Kod ile İndirin</div>
              <div className="text-gray-600 fs-7 mb-0">Uygulamayı indirmek için QR kodu okutabilirsiniz</div>
            </div>
          </div>
        </div>
      )}
      {/* end::QR */}
    </div>
  );
}

export function AuthPage() {
  const location = useLocation();
  const { pathname } = location;
  const trimmedPathname = pathname.replace(/^\//, '');
  const [isMobileDevice, setIsMobileDevice] = useState(false);

  useEffect(() => {
    document.body.classList.add('bg-white');
    setIsMobileDevice(isMobileOrTabletDevice());
    return () => {
      document.body.classList.remove('bg-white');
    };
  }, []);

  return (
    <div
      className="auth-page d-flex flex-column flex-column-fluid bgi-position-y-bottom position-x-center bgi-no-repeat bgi-size-contain bgi-attachment-fixed"
      style={{
        backgroundImage: `url(${toAbsoluteUrl('/media/illustrations/progress-hd.png')})`,
      }}
    >
      <AppDownloadBanner />
      {/* begin::Content */}
      <div className="d-flex flex-center flex-column-fluid p-10 pb-lg-20">
        {/* begin::Layout — yan yana yalnızca xxl (≥1400px) ve tablet değilse */}
        <div
          className={clsx(
            'd-flex flex-column align-items-center justify-content-center gap-8 w-100',
            !isMobileDevice && 'flex-xxl-row align-items-xxl-stretch'
          )}
        >
          {/* begin::Spacer (keeps login form centered on large screens) */}
          {!isMobileDevice && trimmedPathname === 'auth/login' && <div className="d-none d-xxl-block w-xxl-350px flex-shrink-0" aria-hidden="true" />}
          {/* end::Spacer */}

          {/* begin::Wrapper */}
          <div className="w-100 w-xxl-425px bg-white rounded shadow-sm p-8 p-lg-12">
            <Switch>
              <Route path="/auth/login" component={Login} />
              <Route path="/auth/registration" component={Registration} />
              <Route path="/auth/forgot-password" component={ForgotPassword} />
              <Route path="/auth/change-password" component={ChangePassword} />
              <Route path="/yenisifre/:hash" component={NewPassword} />
              {trimmedPathname !== '/auth/login' && <Redirect from="/auth" exact={true} to={`/dashboard?redirect=${trimmedPathname}`} />}
              <Redirect from="/auth" exact={true} to={`/auth/login?r=${trimmedPathname}`} />
              {trimmedPathname ? <Redirect to={`/auth/login?redirect=${trimmedPathname}`} /> : <Redirect to={`/auth/login`} />}
            </Switch>
          </div>
          {/* end::Wrapper */}

          {/* begin::Side card */}
          {trimmedPathname === 'auth/login' && <AuthSideCard isMobileDevice={isMobileDevice} />}
          {/* end::Side card */}
        </div>
        {/* end::Layout */}
      </div>
      {/* end::Content */}
      {/* begin::Footer */}
      {/* end::Footer */}
    </div>
  );
}
